/**
 * Dashboard — landing page for every authenticated role. Four glances:
 *   1. Fleet health donut over the user's visible boxes
 *   2. Total inventory currently sitting in those boxes
 *   3. Pending refill tasks (items below min)
 *   4. The fleet map (embedded — Jason 2026-06-03)
 *
 * The donut consumes `state_counts` from useScopedBoxes (the hook
 * synthesises the counts client-side over the SCOPED boxes the API
 * returns, so a customer/vendor sees their own fleet's mix, not the
 * platform-wide one). Inventory + refill totals come from the new
 * `/api/dashboard` endpoint, which scopes server-side via the same
 * `vw_user_scu_access` view.
 *
 * The map is the existing MapPage rendered inline so the dashboard
 * IS the map — no "See on map" jump needed. The /map route still
 * exists as an alias for old bookmarks and renders the same component.
 */
import {
  ArchiveBoxIcon,
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { Link } from "react-router-dom";

import LoadingSpinner from "../components/LoadingSpinner";
import UnderConstructionBanner from "../components/UnderConstructionBanner";
import ConnectivityDonut, {
  ConnectivityLegend,
  type DonutSlice,
} from "../components/dashboard/ConnectivityDonut";
import StateDonut, {
  type DashboardBucket,
} from "../components/dashboard/StateDonut";
import StateLegend from "../components/dashboard/StateLegend";
import TagHealthDonut, {
  TagHealthLegend,
} from "../components/dashboard/TagHealthDonut";
import { useAuth } from "../contexts/AuthContext";
import { useDashboardTotals } from "../hooks/useDashboardTotals";
import { useScopedBoxes } from "../hooks/useScopedBoxes";
import { useTagHealth } from "../hooks/useTagHealth";
import { fmtAgo } from "../lib/format";

import { FleetMap } from "./MapPage";

const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  to?: string;
}> = ({ label, value, hint, icon: Icon, to }) => {
  const body = (
    <div className="bg-white border border-secondary-200 rounded-md p-4 flex items-center gap-4 hover:border-primary-300 transition-colors">
      <Icon className="h-8 w-8 text-primary-600 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-secondary-500 font-semibold">
          {label}
        </div>
        <div className="text-2xl font-bold text-secondary-900 mt-0.5">
          {value}
        </div>
        {hint && (
          <div className="text-xs text-secondary-500 mt-0.5">{hint}</div>
        )}
      </div>
    </div>
  );
  return to ? (
    <Link to={to} className="block">
      {body}
    </Link>
  ) : (
    body
  );
};

export const DashboardPage: React.FC = () => {
  const { can } = useAuth();
  // Money is hidden from users without pricing.view (the server also nulls
  // mtd_value for them; this keeps the tile's hint clean).
  const showPricing = can("pricing.view");
  // Each dashboard tile is gated on its own view capability (migration 128),
  // toggleable per role + per user in Settings. Baseline grants: fleet_health
  // -> everyone; tag_health / starlink / peplink -> platform roles.
  const canFleetHealth = can("dashboard.fleet_health");
  const canTagHealth = can("dashboard.tag_health");
  const canStarlink = can("dashboard.starlink");
  const canPeplink = can("dashboard.peplink");
  // One /api/boxes fetch for the whole dashboard: the donut consumes this
  // scoped snapshot AND the embedded <FleetMap> reuses the same result rather
  // than fetching a second time.
  const scopedBoxes = useScopedBoxes();
  const { snapshot, loading, error, refresh: refreshBoxes } = scopedBoxes;
  const {
    totals,
    loading: totalsLoading,
    error: totalsError,
    refresh: refreshTotals,
  } = useDashboardTotals();
  const {
    data: tagHealth,
    loading: tagLoading,
    error: tagError,
    refresh: refreshTagHealth,
  } = useTagHealth();
  const refreshing = loading || totalsLoading || tagLoading;
  // Tick to force the "Xs ago" labels to re-render every second.
  // Lightweight — single setInterval at the page level.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  // "✓ Refreshed Xs ago" badge — set when an explicit refresh completes.
  // null until the user clicks Refresh at least once; auto-clears after
  // 8s so it doesn't linger forever.
  const [refreshedAt, setRefreshedAt] = React.useState<Date | null>(null);
  const onRefresh = async (): Promise<void> => {
    await Promise.all([refreshBoxes(), refreshTotals(), refreshTagHealth()]);
    setRefreshedAt(new Date());
  };
  React.useEffect(() => {
    if (refreshedAt == null) return;
    const id = window.setTimeout(() => setRefreshedAt(null), 8000);
    return () => window.clearTimeout(id);
  }, [refreshedAt]);

  const fmtN = (n: number | null | undefined): string =>
    n == null ? "—" : n.toLocaleString("en-US");

  // Build dashboard buckets from the scoped boxes. Stacked boxes are
  // pulled OUT of their underlying state (an UP-and-stacked box would
  // otherwise be counted as UP) and into the synthetic STACKED bucket
  // so the donut shows them as their own slice.
  const buckets: Partial<Record<DashboardBucket, number>> = React.useMemo(() => {
    const acc: Partial<Record<DashboardBucket, number>> = {};
    for (const b of snapshot?.boxes ?? []) {
      const key: DashboardBucket = b.is_stacked ? "STACKED" : b.state;
      acc[key] = (acc[key] ?? 0) + 1;
    }
    return acc;
  }, [snapshot]);

  // Platform-only connectivity tiles (Starlink / Peplink), aggregated
  // client-side over the same scoped snapshot. Colors reuse the app's
  // state palette: green = online (STATE_HEX.UP tone), red = offline
  // (DOWN tone), grey = unknown / no-data (UNTOUCHED tone).
  const CONN_ONLINE_HEX = "#16a34a"; // green-600
  const CONN_OFFLINE_HEX = "#dc2626"; // red-600
  const CONN_NEUTRAL_HEX = "#9ca3af"; // gray-400
  const starlinkSlices: DonutSlice[] = React.useMemo(() => {
    let online = 0;
    let offline = 0;
    let noData = 0;
    for (const b of snapshot?.boxes ?? []) {
      if (b.starlink?.reachable === true) online += 1;
      else if (b.starlink != null) offline += 1;
      else noData += 1;
    }
    return [
      { label: "Online", value: online, color: CONN_ONLINE_HEX },
      { label: "Offline", value: offline, color: CONN_OFFLINE_HEX },
      { label: "No data", value: noData, color: CONN_NEUTRAL_HEX },
    ];
  }, [snapshot]);
  const peplinkSlices: DonutSlice[] = React.useMemo(() => {
    let online = 0;
    let offline = 0;
    let unknown = 0;
    for (const b of snapshot?.boxes ?? []) {
      if (b.peplink_status === "online") online += 1;
      else if (b.peplink_status === "offline") offline += 1;
      else unknown += 1; // "unknown" or null
    }
    return [
      { label: "Online", value: online, color: CONN_ONLINE_HEX },
      { label: "Offline", value: offline, color: CONN_OFFLINE_HEX },
      { label: "Unknown", value: unknown, color: CONN_NEUTRAL_HEX },
    ];
  }, [snapshot]);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <header className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold">Dashboard</h1>
          {/* Data-age line — surfaces the snapshot's actual generation
              time so it's obvious when the runner hasn't cycled in a
              while (Refresh re-fetches the file but doesn't trigger a
              new cycle). */}
          {snapshot?.generated_utc && (
            <div className="text-[11px] text-secondary-500 mt-0.5">
              data generated {fmtAgo(snapshot.generated_utc)} ago
            </div>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {/* Completion indicator — green ✓ after a manual refresh
              completes; auto-clears after 8s. */}
          {refreshedAt && !refreshing && (
            <span
              className="text-xs text-success-700"
              title={`Refreshed at ${refreshedAt.toLocaleTimeString()}`}
            >
              ✓ refreshed {fmtAgo(refreshedAt.toISOString())} ago
            </span>
          )}
          <button
            type="button"
            onClick={() => void onRefresh()}
            disabled={refreshing}
            className="px-2.5 py-1 text-xs rounded border border-secondary-300 bg-white hover:bg-secondary-50 active:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Re-fetch boxes snapshot + dashboard totals. Note: does not trigger a new outage-builder cycle."
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      <UnderConstructionBanner />

      {error && (
        <div className="mb-4 text-xs text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          Couldn't load fleet snapshot: {error.message}
        </div>
      )}

      {/* Fleet health + Tag health tiles: side-by-side on lg:+, stacked
          on mobile. Each tile is donut + legend side-by-side internally,
          and gated on its own view capability (migration 128) so it can be
          toggled per role + per user. The row is hidden when neither cap is
          held. No "See on map" link any more — the map is embedded below. */}
      {(canFleetHealth || canTagHealth) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {canFleetHealth && (
            <section className="bg-white border border-secondary-200 rounded-md p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
                  Fleet health
                </h2>
              </div>
              {loading && !snapshot ? (
                <div className="py-10 flex justify-center">
                  <LoadingSpinner label="loading fleet" />
                </div>
              ) : (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="shrink-0">
                    <StateDonut counts={buckets} size={220} />
                  </div>
                  <div className="flex-1 w-full max-w-md">
                    <StateLegend counts={buckets} />
                  </div>
                </div>
              )}
            </section>
          )}

          {canTagHealth && (
            <section className="bg-white border border-secondary-200 rounded-md p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
                  Tag health
                </h2>
                <div className="text-[11px] text-secondary-500">all-time</div>
              </div>
              {tagError && (
                <div className="mb-3 text-xs text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
                  Couldn't load tag health: {tagError.message}
                </div>
              )}
              {tagLoading && !tagHealth ? (
                <div className="py-10 flex justify-center">
                  <LoadingSpinner label="loading tags" />
                </div>
              ) : (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="shrink-0">
                    <TagHealthDonut
                      healthy={tagHealth?.healthy ?? 0}
                      abnormal={tagHealth?.abnormal ?? 0}
                      size={220}
                    />
                  </div>
                  <div className="flex-1 w-full max-w-md">
                    <TagHealthLegend
                      healthy={tagHealth?.healthy ?? 0}
                      abnormal={tagHealth?.abnormal ?? 0}
                    />
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* Connectivity row — Starlink + Peplink health, aggregated client-side
          over the scoped snapshot. Each tile is gated on its own view
          capability (migration 128; baseline platform-only) rather than a
          hardcoded platform role check, so it can be granted to a customer /
          vendor role or user. The row is hidden when neither cap is held; the
          2-up layout is preserved when both show. */}
      {(canStarlink || canPeplink) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {canStarlink && (
            <section className="bg-white border border-secondary-200 rounded-md p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
                  Starlink health
                </h2>
              </div>
              {loading && !snapshot ? (
                <div className="py-10 flex justify-center">
                  <LoadingSpinner label="loading starlink" />
                </div>
              ) : (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="shrink-0">
                    <ConnectivityDonut slices={starlinkSlices} size={220} />
                  </div>
                  <div className="flex-1 w-full max-w-md">
                    <ConnectivityLegend slices={starlinkSlices} />
                  </div>
                </div>
              )}
            </section>
          )}

          {canPeplink && (
            <section className="bg-white border border-secondary-200 rounded-md p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
                  Peplink connectivity
                </h2>
              </div>
              {loading && !snapshot ? (
                <div className="py-10 flex justify-center">
                  <LoadingSpinner label="loading peplink" />
                </div>
              ) : (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="shrink-0">
                    <ConnectivityDonut slices={peplinkSlices} size={220} />
                  </div>
                  <div className="flex-1 w-full max-w-md">
                    <ConnectivityLegend slices={peplinkSlices} />
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {totalsError && (
        <div className="mb-4 text-xs text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          Couldn't load inventory totals: {totalsError.message}
        </div>
      )}

      {/* KPI row. Smartbox count lives in the Fleet Health donut above
          so we drop the redundant SmartBoxes tile here. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          label="Total inventory"
          value={
            totalsLoading && !totals ? "…" : fmtN(totals?.total_inventory)
          }
          hint="items currently in your boxes"
          icon={ArchiveBoxIcon}
          to="/inventory"
        />
        <KpiCard
          label="Pending refills"
          value={totalsLoading && !totals ? "…" : fmtN(totals?.refill_lines)}
          hint={
            totals
              ? `across ${fmtN(totals.refill_boxes)} smartbox${totals.refill_boxes === 1 ? "" : "es"}`
              : "items below minimum"
          }
          icon={ClipboardDocumentCheckIcon}
          to="/refill"
        />
        <KpiCard
          label="Departures (MTD)"
          value={totalsLoading && !totals ? "…" : fmtN(totals?.mtd_departures)}
          // Pricing is often blank — left for vendor/customer
          // agreements to fill in over time. Don't frame unpriced
          // lines as a deficit ("≥ $X"); just surface the priced
          // dollars when there are any, plain.
          hint={
            !totals
              ? "confirmed + partial · this month"
              : showPricing && totals.mtd_value != null && totals.mtd_value > 0
                ? `${new Intl.NumberFormat(undefined, {
                    style: "currency",
                    currency: totals.mtd_value_currency ?? "USD",
                    maximumFractionDigits: 0,
                  }).format(totals.mtd_value)} priced · this month`
                : "this month"
          }
          icon={ArrowRightOnRectangleIcon}
          to="/departures"
        />
        <KpiCard
          label="Arrivals (MTD)"
          value={totalsLoading && !totals ? "…" : fmtN(totals?.mtd_arrivals)}
          hint="new + re-arrivals · this month"
          icon={ArrowLeftOnRectangleIcon}
          to="/arrivals"
        />
      </div>

      {/* Map — inlined so the Dashboard IS the operator landing view.
          MapPage carries its own header / chips / refresh button.
          MapPage's root is `h-full flex flex-col` with the Leaflet
          area as `flex-1`, so the embed wrapper MUST have a concrete
          height — otherwise the map area collapses to zero (visible
          on mobile as "I only see chips"). 80vh tracks the viewport
          on both desktop and small screens. */}
      <section className="mt-6 -mx-4 md:-mx-6 h-[80vh]">
        <FleetMap {...scopedBoxes} />
      </section>
    </div>
  );
};

export default DashboardPage;
